/**
 * The order of operations that takes a payment, with the SDK and the
 * server injected so the ordering itself can be tested.
 *
 * Three guarantees live here, and each one is a way money or bookings
 * go wrong if it is broken:
 *
 * 1. VALIDATE BEFORE CREATING. `elements.submit()` runs first, so a
 *    mistyped card never creates a PaymentIntent. Creating one per
 *    fumbled keystroke would litter the account with intents that a
 *    reaper then has to chase.
 *
 * 2. LOCK BEFORE COMMITTING. From the moment the intent is created,
 *    money can move, and the widget must refuse to close — tearing the
 *    Element down underneath `confirmPayment` destroys the call's own
 *    context and the customer watches the widget vanish mid-charge.
 *
 * 3. RELEASE ON EVERY TERMINAL OUTCOME — success included — but never
 *    in a `finally`. The distinction matters in both directions. A
 *    `finally` would unlock while a redirect method (Klarna, Affirm) is
 *    navigating the page away, letting the widget be torn down while the
 *    customer is at the provider. But forgetting to unlock on SUCCESS is
 *    just as bad the other way: nothing else ever lowers this, so the
 *    widget stays unclosable for the rest of the page's life and the
 *    Element's portal is leaked onto the salon's page. Only the
 *    genuinely non-terminal case — `redirecting` — stays locked.
 */

export interface ConfirmPaymentDeps {
  /** Validates and collects; returns an error instead of throwing. */
  submit: () => Promise<{ error?: { message?: string } }>;
  /** Creates the PaymentIntent server-side. Only called once committed. */
  createIntent: () => Promise<{ clientSecret: string; returnUrl?: string }>;
  confirm: (args: {
    clientSecret: string;
    returnUrl?: string;
  }) => Promise<{ error?: { message?: string }; paymentIntent?: { id: string; status: string } }>;
  /** Raised before the intent exists; lowered only on a terminal failure. */
  setLocked: (locked: boolean) => void;
}

export type ConfirmPaymentOutcome =
  /** Settled in place — a card. */
  | { kind: 'paid'; paymentIntentId: string; status: string }
  /** The customer's input was rejected; nothing was created. */
  | { kind: 'invalid'; message: string }
  /** Stripe refused the payment. The intent exists and is terminal. */
  | { kind: 'declined'; message: string }
  /**
   * Something threw. The lock is released; the cause is unknown.
   *
   * `code` carries the SERVER's error code when there was one, because
   * some of them are recoverable and the caller cannot tell from the
   * message. CART_EXPIRED is the live example: the hold lapsed while
   * the customer sat on the payment step, and the right answer is to
   * send them back to pick a new time — but with the code dropped, it
   * rendered as an inline error inside the payment form, telling them
   * to pick a new time while offering no control that does so, and a
   * Pay button that would fail identically forever.
   */
  | { kind: 'error'; message: string; code?: string }
  /**
   * The confirm call is taking the customer to another site. This
   * resolves only on paths where the SDK reports it rather than
   * navigating instantly; the lock deliberately STAYS raised.
   */
  | { kind: 'redirecting' };

const GENERIC_INVALID = 'Please check your payment details.';
const GENERIC_DECLINED = 'That payment did not go through.';
const GENERIC_ERROR = 'Something went wrong taking payment.';

export async function runConfirmPaymentFlow(
  deps: ConfirmPaymentDeps,
): Promise<ConfirmPaymentOutcome> {
  // 1 — validate. No server call, no intent, nothing to clean up.
  let submitted: { error?: { message?: string } };
  try {
    submitted = await deps.submit();
  } catch (err) {
    return { kind: 'error', message: messageOf(err, GENERIC_ERROR) };
  }
  if (submitted.error) {
    return { kind: 'invalid', message: submitted.error.message ?? GENERIC_INVALID };
  }

  // 2 — lock, THEN create. Raised before the intent exists, so there is
  // no instant in which a PaymentIntent is live and the widget is still
  // closeable.
  deps.setLocked(true);

  let intent: { clientSecret: string; returnUrl?: string };
  try {
    intent = await deps.createIntent();
  } catch (err) {
    // Nothing was confirmed and no money can move, so it is safe — and
    // necessary — to hand the customer back.
    deps.setLocked(false);
    return { kind: 'error', message: messageOf(err, GENERIC_ERROR), code: codeOf(err) };
  }

  try {
    const result = await deps.confirm({
      clientSecret: intent.clientSecret,
      returnUrl: intent.returnUrl,
    });
    if (result.error) {
      // Terminal: Stripe has answered. The customer may try again.
      deps.setLocked(false);
      return { kind: 'declined', message: result.error.message ?? GENERIC_DECLINED };
    }
    if (result.paymentIntent) {
      // Terminal: Stripe is done and the browser is staying here.
      //
      // An earlier version held the lock through the booking that
      // follows, reasoning that closing mid-hand-off was worse. It is
      // not: nothing ever released it afterwards, so the widget became
      // permanently unclosable on every successful payment, and the
      // Element's portal was leaked onto the salon's page because the
      // cleanup skips teardown while the lock is raised. An unclosable
      // widget is a worse outcome than a closed one whose money is
      // already taken and server-reconciled — the booking call survives
      // a close, and any retry finds the payment.
      deps.setLocked(false);
      return {
        kind: 'paid',
        paymentIntentId: result.paymentIntent.id,
        status: result.paymentIntent.status,
      };
    }
    // No error and no intent means the SDK is navigating. Stay locked.
    return { kind: 'redirecting' };
  } catch (err) {
    // A throw here is ambiguous — the confirm may have reached Stripe.
    // Release so the customer is not stranded, and report it; the server
    // side is what reconciles whether money actually moved.
    deps.setLocked(false);
    return { kind: 'error', message: messageOf(err, GENERIC_ERROR) };
  }
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * The server's error code, when the thrown thing carries one. Read
 * structurally rather than by importing the API error class, so this
 * module stays free of the transport it is driven by.
 */
function codeOf(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'string' && code) return code;
  }
  return undefined;
}
